import {ButtonBar, useSnackbar} from "@dain/mui-components";
import {Grid, Typography} from "@mui/material";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {DASHBOARD} from "../../../AppRoutes";
import {useClientLocator} from "../../../client/ApiClientLocator";
import {FormSessionCreateResultDto, FormSessionPrefillRequestDto,} from "../../../client/formsession/FormSessionApiClient";
import Button from "../../../common/button/Button";
import {CommonTranslKey} from "../../../common/CommonTranslKey";
import InformationDialog from "../../../common/dialog/InformationDialog";
import Page from "../../../common/page/Page";
import {Variant} from "../../../common/Variant";
import {FormSessionTranslKey} from "../FormSessionTranslKey";
import {useFormSessionStateQuery} from "./FormSessionStateQuery";
import {usePrefillRequestsQuery} from "./PrefillRequestQuery";
import PrefillRequestsStepper from "./PrefillRequestsStepper";
import {
    getOptionalPrefillRequests,
    getOptionalPrefillStatus,
    getRequiredPrefillRequests,
    getRequiredPrefillStatus,
    PrefillStatus
} from "./PrefillStatus";

export interface FormSessionOpenerNiaUserPageProps {
    documentType: string | null;
    documentVersion: string | null;
    openerParams: Record<string, string>;
}

export function FormSessionOpenerNiaUserPage(props: Readonly<FormSessionOpenerNiaUserPageProps>) {
    const {documentType, documentVersion, openerParams} = props;

    const isInitialized = useRef(false);
    const {t} = useTranslation();
    const {formSessionClient} = useClientLocator();
    const navigate = useNavigate();
    const snackbar = useSnackbar();

    const [requiredPrefillRequests, setRequiredPrefillRequests] = useState<FormSessionPrefillRequestDto[]>();
    const [optionalPrefillRequests, setOptionalPrefillRequests] = useState<FormSessionPrefillRequestDto[]>();
    const [requiredPrefillStatus, setRequiredPrefillStatus] = useState<PrefillStatus>();
    const [optionalPrefillStatus, setOptionalPrefillStatus] = useState<PrefillStatus>();
    const [formSessionId, setFormSessionId] = useState<number>();
    const [formSessionRedirectUrl, setFormSessionRedirectUrl] = useState<string>();

    const [hasRedirected, setHasRedirected] = useState<boolean>(false);

    const initializePrefillRequests = useCallback((formSessionCreateResult: FormSessionCreateResultDto) => {
        const requiredRequests = getRequiredPrefillRequests(formSessionCreateResult.prefillRequests);
        setRequiredPrefillRequests(requiredRequests);
        setRequiredPrefillStatus(getRequiredPrefillStatus(requiredRequests));

        const optionalRequests = getOptionalPrefillRequests(formSessionCreateResult.prefillRequests);
        setOptionalPrefillRequests(optionalRequests);
        setOptionalPrefillStatus(getOptionalPrefillStatus(optionalRequests));
    }, []);

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;

            if (documentType && documentVersion) {
                const formSessionCreateDto = {documentType, documentVersion, openerParams: Object.keys(openerParams).length > 0 ? openerParams : undefined};
                formSessionClient.createSessionAndReturnRedirectInfo(formSessionCreateDto)
                        .then((response) => {
                            setFormSessionId(response.formSession.id);
                            setFormSessionRedirectUrl(response.redirectUrl);
                            initializePrefillRequests(response);
                        })
                        .catch((error) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error));
            } else {
                snackbar.error(t(FormSessionTranslKey.MISSING_PARAMETERS), null);
                return;
            }
        }
    }, [documentType, documentVersion, openerParams, initializePrefillRequests, formSessionClient, snackbar, t]);

    const onlyRequiredReady = requiredPrefillStatus === PrefillStatus.ALL_OBTAINED && optionalPrefillStatus !== PrefillStatus.ALL_OBTAINED;
    const bothRequiredAndOptionalReady = requiredPrefillStatus === PrefillStatus.ALL_OBTAINED && optionalPrefillStatus === PrefillStatus.ALL_OBTAINED;
    const isAnyRequestInProgress = requiredPrefillStatus === PrefillStatus.IN_PROGRESS || optionalPrefillStatus === PrefillStatus.IN_PROGRESS;

    const {data: prefillRequestsResult} = usePrefillRequestsQuery(formSessionId, isAnyRequestInProgress, formSessionClient);
    useEffect(() => {
        if (prefillRequestsResult) {
            setRequiredPrefillRequests(prefillRequestsResult.requiredPrefillRequests);
            setRequiredPrefillStatus(prefillRequestsResult.requiredPrefillStatus);
            setOptionalPrefillRequests(prefillRequestsResult.optionalPrefillRequests);
            setOptionalPrefillStatus(prefillRequestsResult.optionalPrefillStatus);
        }
    }, [prefillRequestsResult, prefillRequestsResult?.requiredPrefillRequests, prefillRequestsResult?.requiredPrefillStatus, prefillRequestsResult?.optionalPrefillRequests, prefillRequestsResult?.optionalPrefillStatus]);


    const requiredPrefillStatusIsObtainedOrFailed = requiredPrefillStatus === PrefillStatus.ALL_OBTAINED || requiredPrefillStatus === PrefillStatus.FAILED;
    const {isFormSessionObtained, isFormSessionFailed} = useFormSessionStateQuery(formSessionId, requiredPrefillStatusIsObtainedOrFailed);

    const redirectToForm = useCallback(() => {
        if (hasRedirected) {
            return;
        }
        
        setHasRedirected(true);
        if (formSessionRedirectUrl) {
            window.location.href = formSessionRedirectUrl;
        }
    }, [formSessionRedirectUrl, hasRedirected]);

    const continueToNewFormSession = useCallback(() => {
        if (isFormSessionObtained) {
            redirectToForm();
        }
    }, [isFormSessionObtained, redirectToForm]);

    useEffect(() => {
        const noPrefillRequests = requiredPrefillRequests?.length === 0 && optionalPrefillRequests?.length === 0;
        if (isFormSessionObtained && noPrefillRequests) {
            redirectToForm();
        }
    }, [isFormSessionObtained, requiredPrefillRequests, optionalPrefillRequests, redirectToForm]);

    useEffect(() => {
        const allPrefillRequestsObtained = requiredPrefillStatus === PrefillStatus.ALL_OBTAINED && optionalPrefillStatus === PrefillStatus.ALL_OBTAINED;
        if (isFormSessionObtained && allPrefillRequestsObtained) {
            const timeoutId = setTimeout(() => {
                redirectToForm();
            }, 5000);
            return () => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
            };
        }
    }, [isFormSessionObtained, requiredPrefillStatus, optionalPrefillStatus, redirectToForm]);

    function navigateToDashboard() {
        navigate(DASHBOARD);
    }

    return (
        <Page title={t(FormSessionTranslKey.FORM_OPENING)}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h5">
                        {t(FormSessionTranslKey.PERSON_DATA_RETRIEVAL_TITLE)}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" sx={{textAlign: "justify"}}>
                        {t(FormSessionTranslKey.PERSON_DATA_RETRIEVAL_DETAIL)}
                    </Typography>
                </Grid>

                {requiredPrefillRequests &&
                        <Grid item xs={12}>
                            <PrefillRequestsStepper prefillRequests={requiredPrefillRequests!}
                                    prefillRequestsTypeTitle={t(FormSessionTranslKey.REQUIRED_PREFILL_INFORMATION_SYSTEMS_TITLE)}
                                    prefillRequestsTypeDetail={t(FormSessionTranslKey.REQUIRED_PREFILL_INFORMATION_SYSTEMS_DETAIL)}/>
                        </Grid>
                }

                {optionalPrefillRequests &&
                        <Grid item xs={12}>
                            <PrefillRequestsStepper prefillRequests={optionalPrefillRequests}
                                    prefillRequestsTypeTitle={t(FormSessionTranslKey.OPTIONAL_PREFILL_INFORMATION_SYSTEMS_TITLE)}
                                    prefillRequestsTypeDetail={t(FormSessionTranslKey.OPTIONAL_PREFILL_INFORMATION_SYSTEMS_DETAIL)}/>
                        </Grid>
                }

                {onlyRequiredReady &&
                        <>
                            <Grid item xs={12}>
                                <ButtonBar>
                                    <Button variant={Variant.SECONDARY}
                                            onClick={continueToNewFormSession}
                                            disabled={!isFormSessionObtained}
                                            inProgress={!isFormSessionObtained}>
                                        {t(FormSessionTranslKey.CONTINUE_WITHOUT_WAITING)}
                                    </Button>
                                </ButtonBar>
                            </Grid>
                        </>
                }

                {bothRequiredAndOptionalReady &&
                        <>
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{textAlign: "justify", fontWeight: "bold"}}>
                                    {t(FormSessionTranslKey.PERSON_DATA_RETRIEVAL_SUCCESS)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <ButtonBar>
                                    <Button variant={Variant.PRIMARY}
                                            onClick={continueToNewFormSession}
                                            disabled={!isFormSessionObtained}
                                            inProgress={!isFormSessionObtained}>
                                        {t(FormSessionTranslKey.CONTINUE_TO_FORM)}
                                    </Button>
                                </ButtonBar>
                            </Grid>
                        </>
                }

                {requiredPrefillStatus === PrefillStatus.FAILED &&
                        <Grid item xs={12} justifyItems="center" alignItems="center">
                            <Typography variant="body2" sx={{fontWeight: "bold"}}>
                                {t(FormSessionTranslKey.REQUIRED_PREFILL_REQUESTS_FAILED)}
                            </Typography>
                        </Grid>
                }
            </Grid>

            <InformationDialog
                    open={requiredPrefillStatus === PrefillStatus.FAILED || isFormSessionFailed}
                    onClose={() => navigateToDashboard()}
                    title={t(FormSessionTranslKey.REQUIRED_PREFILL_REQUESTS_FAILED_DIALOG_TITLE)}
                    primaryButton={{
                        onClick: () => navigateToDashboard(),
                        title: t(FormSessionTranslKey.GO_TO_DASHBOARD)
                    }}>
                {t(FormSessionTranslKey.REQUIRED_PREFILL_REQUESTS_FAILED_DIALOG_MESSAGE)}
            </InformationDialog>
        </Page>
    );
}
